import styles from "./AccountSettingScreen.module.css";
import { Page, PageHeader } from "../../components/layout/Page";
import Button from "../../components/ui/Button";
import LoadingScreen from "../common/LoadingScreen";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import BusinessContainer from "../common/BusinessContainer";
import ProfileIcon from "./ProfileIcon";
import { onAuthStateChanged } from "firebase/auth";
import { deleteCurrentUser, getLoginProvider, logout } from "./AuthManager";
import { auth } from "../../configurations/firebaseConfig";
import BasicMenu, { BasicMenuButton } from "../common/BasicMenu";
import {
  fetchUserInformation as fetchUserInformationFromAPI,
  updateProfileImage as updateProfileImageFromAPI,
} from "./authAPI";
import { deleteProfileImage as deleteProfileImageFromAPI } from "./authAPI";
import { updateUsername as updateUsernameFromAPI } from "./authAPI";
import { deleteUser as deleteUserFromAPI } from "./authAPI";
import { convertFileToJpg } from "../common/files";
import { Status } from "../common/Status";
import Toast from "../../components/ui/Toast";
import { profileImageUrl } from "../common/contants";
import {
  getLastUpdatedProfileImage,
  setLastUpdatedProfileImage,
} from "../settings/util/userSettings";
import ValueChangeDialog from "../common/ValueChangeDialog";
import OnevocaDialog from "../common/OnevocaDialog";
import { ServerStatus, fetchServerStatus } from "../common/serverStatus";
import UserInformation from "./UserInformation";
import { getRankColor } from "./rankColor";
import { localizedDateSimpleString } from "../common/util/date";

export default function AccountSettingScreen() {
  const navigate = useNavigate();

  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [profileIconSrc, setProfileIconSrc] = useState<string>();

  const [username, setUsername] = useState<string | null>(null);
  const [email, setEmail] = useState<string>();
  const [createdAt, setCreatedAt] = useState<string>();
  const [userInformation, setUserInformaton] = useState<UserInformation>();
  const [isLoading, setIsLoading] = useState(false);

  const [serverStatus, setServerStatus] = useState<ServerStatus>();
  const [userInformationFetchStatus, setUserInformationFetchStatus] = useState(
    Status.Ready
  );
  const [updateProfileImageStatus, setUpdateProfileImageStatus] = useState(
    Status.Ready
  );
  const [profileImageDeleteStatus, setProfileImageDeleteStatus] = useState(
    Status.Ready
  );
  const [usernameUpdateStatus, setUsernameUpdateStatus] = useState(
    Status.Ready
  );
  const [userDeleteStatus, setUserDeleteStatus] = useState(Status.Ready);

  const [openDeleteUserDialog, setOpenDeleteUserDialog] = useState(false);
  const [openEditUsernameDialog, setOpenEditUsernameDialog] = useState(false);
  const [openProfileImageChangeMenu, setOpenProfileImageChangeMenu] =
    useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const profileImageChangeButtons: BasicMenuButton[] = [
    {
      title: t("ProfileImageUploadButton"),
      handleOnClose: () => {
        setOpenProfileImageChangeMenu(false);
        fileInputRef.current?.click();
      },
    },
    {
      title: t("ProfileImageRemoveButton"),
      handleOnClose: () => {
        setOpenProfileImageChangeMenu(false);
        deleteProfileImage();
      },
    },
  ];

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchUserInformation();
        setProfileIconSrc(
          `${profileImageUrl}${
            user.providerData[0].uid
          }.jpg?t=${getLastUpdatedProfileImage()}`
        );
        checkServerInspect();
      } else {
        logout();
        navigate("/login");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    switch (serverStatus) {
      case ServerStatus.Maintenance:
        navigate("/maintenance");
        break;
      case ServerStatus.Running:
        break;
    }
  }, [navigate, serverStatus]);

  useEffect(() => {
    if (userInformation === undefined) return;
    setUsername(userInformation.username);
    const createdAt = localizedDateSimpleString(userInformation.createdAt);
    setCreatedAt(createdAt);
    setEmail(userInformation.email);
  }, [userInformation]);

  useEffect(() => {
    switch (updateProfileImageStatus) {
      case Status.Ready:
        setIsLoading(false);
        break;
      case Status.Loading:
        setIsLoading(true);
        break;
      case Status.Done:
        setIsLoading(false);
        break;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInformationFetchStatus]);

  useEffect(() => {
    switch (updateProfileImageStatus) {
      case Status.Ready:
        setIsLoading(false);
        break;
      case Status.Loading:
        setIsLoading(true);
        break;
      case Status.Done:
        setIsLoading(false);
        completeUpdateProfileImage();
        break;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateProfileImageStatus]);

  useEffect(() => {
    switch (profileImageDeleteStatus) {
      case Status.Ready:
        setIsLoading(false);
        break;
      case Status.Loading:
        setIsLoading(true);
        break;
      case Status.Done:
        setIsLoading(false);
        completeUpdateProfileImage();
        break;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileImageDeleteStatus]);

  useEffect(() => {
    switch (usernameUpdateStatus) {
      case Status.Ready:
        setIsLoading(false);
        break;
      case Status.Loading:
        setIsLoading(true);
        break;
      case Status.Done:
        setIsLoading(false);
        completeUpdateUsername();
        break;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usernameUpdateStatus]);

  useEffect(() => {
    switch (userDeleteStatus) {
      case Status.Ready:
        setIsLoading(false);
        break;
      case Status.Loading:
        setIsLoading(true);
        break;
      case Status.Done:
        setIsLoading(false);
        completeDeleteUser();
        break;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDeleteStatus]);

  async function checkServerInspect() {
    try {
      setServerStatus(await fetchServerStatus());
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      }
    }
  }

  async function fetchUserInformation() {
    setUserInformationFetchStatus(Status.Loading);
    try {
      const information = await fetchUserInformationFromAPI();
      setUserInformaton(information);
      setUserInformationFetchStatus(Status.Done);
    } catch (error) {
      setUserInformationFetchStatus(Status.Ready);
      if (error instanceof Error) {
        alert(error.message);
      }
    }
  }

  function moveToHome() {
    navigate("/");
  }

  function completeUpdateProfileImage() {
    setToastMessage(t("TermEditedToastMessage"));
    if (auth.currentUser !== null) {
      setLastUpdatedProfileImage(Date.now().toString());
      setProfileIconSrc(undefined);
      setProfileIconSrc(
        `${profileImageUrl}${
          auth.currentUser.providerData[0].uid
        }.jpg?t=${getLastUpdatedProfileImage()}`
      );
    }
  }

  function completeUpdateUsername() {
    setToastMessage(t("TermEditedToastMessage"));
    if (auth.currentUser !== null) {
      setUsername(auth.currentUser.displayName);
    }
  }

  async function completeDeleteUser() {
    await deleteCurrentUser();
    navigate("/login");
  }

  function handleOnClickProfileImageChangeButton(
    e: React.MouseEvent<HTMLDivElement>
  ) {
    setAnchorEl(e.currentTarget);
    setOpenProfileImageChangeMenu(true);
  }

  async function handleImageChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    const uid = auth.currentUser?.providerData[0].uid;
    if (file === undefined || uid === undefined) {
      return;
    }
    updateProfileImage(await convertFileToJpg(file, uid, 150));
  }

  async function updateProfileImage(image: string) {
    setUpdateProfileImageStatus(Status.Loading);
    try {
      await updateProfileImageFromAPI(image);
      setUpdateProfileImageStatus(Status.Done);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
      setUpdateProfileImageStatus(Status.Ready);
    }
  }

  async function deleteProfileImage() {
    setProfileImageDeleteStatus(Status.Loading);
    try {
      await deleteProfileImageFromAPI();
      setProfileImageDeleteStatus(Status.Done);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
      setProfileImageDeleteStatus(Status.Ready);
    }
  }

  async function updateUsername(username: string) {
    setUsernameUpdateStatus(Status.Loading);
    try {
      await updateUsernameFromAPI(username);
      setUsernameUpdateStatus(Status.Done);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
      setUsernameUpdateStatus(Status.Ready);
    }
  }

  async function deleteUser() {
    setUserDeleteStatus(Status.Loading);
    try {
      await deleteUserFromAPI();
      setUserDeleteStatus(Status.Done);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
      setUserDeleteStatus(Status.Ready);
    }
  }

  return (
    <>
      <div className={styles.stack}>
        <div className={styles.container}>
          <Page>
            <PageHeader
              title={t("PersonalInformation")}
              onBack={() =>
                (window.history.state?.idx ?? 0) > 0
                  ? navigate(-1)
                  : moveToHome()
              }
              backLabel={t("Back")}
            />

            <section className={styles.profileCard}>
              <ProfileIcon
                src={profileIconSrc}
                size="72px"
                onCursorPoint={false}
                onClick={() => {}}
              />
              <div className={styles.profileText}>
                <span
                  className={styles.profileName}
                  style={{ color: getRankColor(userInformation?.rank) }}
                >
                  {username}
                </span>
                <span className={styles.profileEmail}>{email}</span>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) =>
                  handleOnClickProfileImageChangeButton(
                    e as unknown as React.MouseEvent<HTMLDivElement>
                  )
                }
              >
                {t("ProfilePictureChangeButton")}
              </Button>
              <input
                type="file"
                accept="image/*,image/heic"
                ref={fileInputRef}
                onChange={handleImageChange}
                style={{ display: "none" }}
              />
            </section>

            <section className={styles.group}>
              <div className={styles.row}>
                <div className={styles.rowText}>
                  <span className={styles.rowLabel}>{t("Username")}</span>
                  <span className={styles.rowValue}>{username}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setOpenEditUsernameDialog(true);
                  }}
                >
                  {t("Edit")}
                </Button>
              </div>
              <div className={styles.row}>
                <div className={styles.rowText}>
                  <span className={styles.rowLabel}>{t("email")}</span>
                  <span className={styles.rowValue}>{email}</span>
                </div>
              </div>
              <div className={styles.row}>
                <div className={styles.rowText}>
                  <span className={styles.rowLabel}>{t("LoginProvider")}</span>
                  <span className={styles.rowValue}>{getLoginProvider()}</span>
                </div>
              </div>
              <div className={styles.row}>
                <div className={styles.rowText}>
                  <span className={styles.rowLabel}>
                    {t("AccountCreatedAt")}
                  </span>
                  <span className={styles.rowValue}>{createdAt}</span>
                </div>
              </div>
            </section>

            <h2 className={styles.groupTitle}>{t("Account")}</h2>
            <section className={styles.group}>
              <div className={styles.row}>
                <div className={styles.rowText}>
                  <span className={styles.rowTitle}>
                    {t("DeleteAccountTitle")}
                  </span>
                  <span className={styles.rowDescription}>
                    {t("DeleteAccountMessage")}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className={styles.dangerButton}
                  onClick={() => {
                    setOpenDeleteUserDialog(true);
                  }}
                >
                  {t("DeleteAccountButton")}
                </Button>
              </div>
            </section>
          </Page>
          <BusinessContainer />
        </div>
        <BasicMenu
          isOpen={openProfileImageChangeMenu === true}
          anchorEl={anchorEl}
          buttons={profileImageChangeButtons}
          handleOnClose={() => {
            setOpenProfileImageChangeMenu(false);
          }}
        />
        <ValueChangeDialog
          open={openEditUsernameDialog === true}
          title={t("ChangeUsername")}
          hint={t("NewUsername")}
          minTextCount={1}
          maxTextCount={20}
          onSave={updateUsername}
          onClose={() => {
            setOpenEditUsernameDialog(false);
          }}
        />
        <OnevocaDialog
          open={openDeleteUserDialog === true}
          title={t("DeleteAccountTitle")}
          message={t("DeleteAccountMessage")}
          buttons={[
            {
              title: t("Cancel"),
              style: "normal",
              onClick: () => {
                setOpenDeleteUserDialog(false);
              },
            },
            {
              title: t("Delete"),
              style: "delete",
              onClick: () => {
                setOpenDeleteUserDialog(false);
                deleteUser();
              },
            },
          ]}
        />
        <Toast
          open={toastMessage !== null}
          message={toastMessage}
          onClose={() => {
            setToastMessage(null);
          }}
        />
        {isLoading === true && <LoadingScreen />}
      </div>
    </>
  );
}
